import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Download,
  ListFilter,
  Search,
  SlidersHorizontal,
} from "lucide-react"
import * as React from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"

import { TRANSACTIONS_TABS } from "@/components/layout/nav"
import { PageHeader } from "@/components/layout/page-header"
import { TabStrip } from "@/components/layout/tab-strip"
import { SortHeader, type SortState } from "@/components/rules/sort-header"
import { Button } from "@/components/ui/button"
import { Inactive } from "@/components/ui/inactive"
import { Input } from "@/components/ui/input"
import { Segmented } from "@/components/ui/segmented"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumeric,
  TableRow,
} from "@/components/ui/table"
import { InfoTip } from "@/components/ui/tooltip"
import {
  listTransactions,
  PAGE_SIZE,
  PERIODS,
  type Transaction,
  type TransactionPeriod,
  type TransactionSort,
  type TransactionsPage,
  TWIN_ID_PREFIX,
} from "@/lib/api"
import {
  EMPTY,
  formatAmount,
  formatDate,
  formatInt,
  formatScore,
  formatText,
  formatTime,
  splitTags,
} from "@/lib/format"
import { cn } from "@/lib/utils"

import { periodParams, readStoredPeriod, resolvePeriod, storePeriod } from "./period"

/**
 * Колонки списка идут в порядке §4 AMEND-10 и с английскими подписями образца.
 * Не переводим: аналитик ходит между настоящим SEON и этой консолью, и
 * «СУММА» вместо `AMOUNT` заставляло бы его каждый раз сверять шапку.
 *
 * Стрелки стоят ровно на семи колонках — тех, что в белом списке ручки
 * (`TRANSACTION_SORTS`). У `USER`, `LABEL`, `ASSIGNED TO` и `TAGS` стрелок нет:
 * ручка по ним сортировать не умеет, и стрелка, которая ничего не переставляет,
 * хуже её отсутствия.
 *
 * «SEON» справа от `Score` — временно, AMEND-60: балл SEON рядом с нашим, чтобы
 * сравнивать глазами. Сверх §4, без стрелки — ручка по ней не сортирует.
 */
const COLUMNS = 13

/**
 * Переключатель периода из §4 — `Custom` и пять окон. `Custom` здесь живой:
 * ручка принимает `from`/`to`, и выключать кнопку, за которой есть работающий
 * запрос, значило бы спрятать имеющуюся функцию.
 */
const PERIOD_OPTIONS = [
  { value: "custom" as const, label: "Custom" },
  ...PERIODS.map((period) => ({ value: period as TransactionPeriod, label: period })),
]

const DEFAULT_SORT: SortState = { sort: "date", order: "desc" }

/** Цвет точки STATE — те же три переменные палитры, что у решений скоринга. */
const STATE_TONES: Record<string, string> = {
  APPROVE: "bg-approve",
  DECLINE: "bg-decline",
  REVIEW: "bg-review",
}

/**
 * Список транзакций (`/transactions`, §4 AMEND-10).
 *
 * Всё, что показано, приходит из `/api/transactions`: строки, счётчик, порядок
 * сортировки, окно периода и страница. Ничего не досчитывается на клиенте —
 * иначе «всего 221 542» и содержимое таблицы могли бы разъехаться, и увидеть
 * это было бы нечем.
 *
 * Пустые колонки оставлены пустыми намеренно. `LABEL` не заполнен ни в одной из
 * 221 542 строк выгрузки, `ASSIGNED TO` — в 193 642 из них; на этих местах стоит
 * прочерк, а не ноль и не выдуманная метка (§7 AMEND-10).
 *
 * За `Filter`, `Statistics`, `View settings`, `Export all` и чекбоксами
 * массового выбора действий нет — они помечены как невыполнимые и объясняют
 * причину по наведению. Кнопка, которая молча ничего не делает, выглядит как
 * поломка; кнопки, которой нет на скриншоте, здесь тоже нет.
 */
export function TransactionsListPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Период — из адреса, иначе запомненный, иначе `1D` (`./period`, AMEND-60).
  const [initial] = React.useState(() => resolvePeriod(searchParams, readStoredPeriod()))
  const [search, setSearch] = React.useState("")
  const [query, setQuery] = React.useState("")
  const [period, setPeriod] = React.useState<TransactionPeriod>(initial.period)
  const [since, setSince] = React.useState(initial.from)
  const [until, setUntil] = React.useState(initial.to)
  const [sortState, setSortState] = React.useState<SortState>(DEFAULT_SORT)
  const [offset, setOffset] = React.useState(0)
  const [reload, setReload] = React.useState(0)
  // Точное число строк — по щелчку, и только для того среза, на котором щёлкнули
  // (AMEND-60 2.3). Ключ среза, а не флаг: смена поиска или периода сама гасит
  // просьбу, без лишнего эффекта и без второго запроса с устаревшим флагом.
  // Сортировку в ключ не кладём — число строк от порядка не зависит.
  const sliceKey = JSON.stringify([query, period, since, until])
  const [exactFor, setExactFor] = React.useState<string | null>(null)
  const exactTotal = exactFor === sliceKey

  const [data, setData] = React.useState<TransactionsPage | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  // Задержка ввода: без неё каждая буква уходит отдельным запросом, а поиск
  // здесь идёт по 221 542 строкам — на длинном слове это была бы очередь из
  // десятка тяжёлых запросов, из которых нужен только последний.
  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(search.trim())
      setOffset(0)
    }, 250)
    return () => window.clearTimeout(timer)
  }, [search])

  // Выбранный период — в хранилище и в адрес: переживает перезагрузку, а ссылку
  // можно переслать. Адрес заменяется, а не дописывается: «назад» в браузере
  // уводит со списка, а не перебирает щелчки по переключателю.
  React.useEffect(() => {
    const chosen = { period, from: since, to: until }
    storePeriod(chosen)
    const next = periodParams(chosen)
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true })
  }, [period, since, until, searchParams, setSearchParams])

  React.useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    listTransactions(
      {
        q: query || undefined,
        period,
        // Границы уходят только у `custom`: у окон они свои, серверные.
        from: period === "custom" && since ? since : undefined,
        to: period === "custom" && until ? until : undefined,
        sort: sortState.sort as TransactionSort,
        order: sortState.order,
        limit: PAGE_SIZE,
        offset,
        exact_total: exactTotal,
      },
      controller.signal,
    )
      .then((result) => {
        setData(result)
        setError(null)
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause instanceof Error ? cause.message : "Request failed")
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [query, period, since, until, sortState, offset, reload, exactTotal])

  const items = data?.items ?? []
  const total = data?.total ?? 0
  // Упёрся подсчёт в потолок — строк больше `total`, и число печатается только
  // с «+». Конец страницы тогда берётся из её строк: за потолком `total` про
  // хвост ничего не знает.
  const capped = data?.total_capped ?? false
  const totalText = capped ? `${formatInt(total)}+` : formatInt(total)
  const from = total === 0 ? 0 : offset + 1
  const to = capped ? offset + items.length : Math.min(offset + PAGE_SIZE, total)

  // Текущий срез списка в адресную строку карточки: её колонка соседей ходит по
  // тем же фильтрам, и без них «предыдущая» в карточке была бы не той строкой,
  // что стояла выше в таблице. Пагинацию не передаём — соседи считаются окном
  // вокруг самой транзакции, а не текущей страницей списка.
  const cardSuffix = React.useMemo(() => {
    const params = new URLSearchParams()
    if (query) params.set("q", query)
    params.set("period", period)
    if (period === "custom" && since) params.set("from", since)
    if (period === "custom" && until) params.set("to", until)
    params.set("sort", sortState.sort)
    params.set("order", sortState.order)
    const encoded = params.toString()
    return encoded ? `?${encoded}` : ""
  }, [query, period, since, until, sortState])

  return (
    <>
      <PageHeader
        title="Transactions"
        // Полоса вкладок — дорога к заливке (AMEND-16 §3 шаг 3). Владелица
        // приходит за строками, а не за загрузчиком, и без вкладки на этом
        // экране экран заливки был бы доступен только по набранному руками
        // адресу — то есть «без меня» не работал бы.
        tabs={<TabStrip label="Transactions tabs" tabs={TRANSACTIONS_TABS} />}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Inactive
              reason="Aggregated transaction statistics are not part of this build."
              size="md"
            >
              <BarChart3 className="h-3.5 w-3.5" strokeWidth={2} />
              Statistics
            </Inactive>
            <Inactive reason="Column layout is fixed in this console." size="md">
              <SlidersHorizontal className="h-3.5 w-3.5" strokeWidth={2} />
              View settings
            </Inactive>
            <Inactive
              reason="Exporting transactions is out of scope: the rows carry personal data and this console does not hand them out as a file."
              size="md"
            >
              <Download className="h-3.5 w-3.5" strokeWidth={2} />
              Export all
            </Inactive>
          </div>
        }
      />

      <div className="p-5">
        <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
          <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle"
                strokeWidth={2}
              />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search"
                aria-label="Search transactions by ID, user ID or email"
                className="w-64 pl-7"
              />
            </div>

            <Inactive reason="Extra filters are not part of this console." size="md">
              <ListFilter className="h-3.5 w-3.5" strokeWidth={2} />
              Filter
            </Inactive>

            <span className="text-xs text-fg-muted">
              {loading && !data ? EMPTY : `${totalText} rows`}
            </span>
            {capped && (
              <InfoTip
                label={`Counting stops at ${formatInt(total)} rows to keep the list fast. Click for the exact number — on a long period it takes a moment.`}
              >
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={loading}
                  onClick={() => setExactFor(sliceKey)}
                >
                  {loading && exactTotal ? "Counting…" : "Exact count"}
                </Button>
              </InfoTip>
            )}

            <Segmented
              className="ml-auto"
              label="Transaction period"
              options={PERIOD_OPTIONS}
              value={period}
              onChange={(value) => {
                setPeriod(value)
                setOffset(0)
              }}
            />
          </div>

          {period === "custom" && (
            <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface-sunken px-4 py-2">
              <label className="stencil-label text-fg-muted" htmlFor="tx-from">
                From
              </label>
              <Input
                id="tx-from"
                type="date"
                value={since}
                onChange={(event) => {
                  setSince(event.target.value)
                  setOffset(0)
                }}
                className="w-36"
              />
              <label className="stencil-label text-fg-muted" htmlFor="tx-to">
                To
              </label>
              <Input
                id="tx-to"
                type="date"
                value={until}
                onChange={(event) => {
                  setUntil(event.target.value)
                  setOffset(0)
                }}
                className="w-36"
              />
              {!since && !until && (
                <span className="text-xs text-fg-muted">
                  Pick at least one bound — until then the list is not filtered by date.
                </span>
              )}
            </div>
          )}

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-9">
                    <BulkCheckbox />
                  </TableHead>
                  <SortHeader
                    column="transaction_id"
                    state={sortState}
                    onSort={onSort(setSortState, setOffset)}
                    className="w-32"
                  >
                    ID
                  </SortHeader>
                  <SortHeader
                    column="fraud_score"
                    state={sortState}
                    onSort={onSort(setSortState, setOffset)}
                    align="right"
                    className="w-20"
                  >
                    Score
                  </SortHeader>
                  <TableHead align="right" className="w-20">SEON</TableHead>
                  <SortHeader
                    column="state"
                    state={sortState}
                    onSort={onSort(setSortState, setOffset)}
                    className="w-28"
                  >
                    State
                  </SortHeader>
                  <SortHeader
                    column="user_id"
                    state={sortState}
                    onSort={onSort(setSortState, setOffset)}
                    className="w-32"
                  >
                    User ID
                  </SortHeader>
                  <TableHead className="w-40">User</TableHead>
                  <SortHeader
                    column="transaction_amount_eur"
                    state={sortState}
                    onSort={onSort(setSortState, setOffset)}
                    align="right"
                    className="w-32"
                  >
                    Amount
                  </SortHeader>
                  <SortHeader
                    column="date"
                    state={sortState}
                    onSort={onSort(setSortState, setOffset)}
                    className="w-28"
                  >
                    Date
                  </SortHeader>
                  <TableHead className="w-24">Label</TableHead>
                  <SortHeader
                    column="transaction_status"
                    state={sortState}
                    onSort={onSort(setSortState, setOffset)}
                    className="w-36"
                  >
                    Transaction status
                  </SortHeader>
                  <TableHead className="w-32">Assigned to</TableHead>
                  <TableHead className="w-48">Tags</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {loading && items.length === 0 && <LoadingRows />}

                {!loading && error && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={COLUMNS} className="px-4 py-10 text-center">
                      <p className="text-sm font-medium text-decline">{error}</p>
                      <p className="mt-1 text-xs text-fg-muted">
                        The Admin API did not answer. The table stays empty on purpose — it is not
                        showing stale rows.
                      </p>
                      <Button className="mt-4" onClick={() => setReload((value) => value + 1)}>
                        Retry
                      </Button>
                    </TableCell>
                  </TableRow>
                )}

                {!loading && !error && items.length === 0 && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={COLUMNS} className="px-4 py-10 text-center">
                      <p className="text-sm text-fg">
                        {query
                          ? "No transactions match this search."
                          : "No transactions in this period."}
                      </p>
                      <p className="mt-1 text-xs text-fg-muted">
                        {query
                          ? "The search looks at transaction ID, user ID and email — not at names or amounts."
                          : "Widen the period on the right, or clear the custom range."}
                      </p>
                    </TableCell>
                  </TableRow>
                )}

                {items.map((row) => (
                  <TransactionRow
                    key={row.id}
                    row={row}
                    suffix={cardSuffix}
                    onOpen={() => navigate(`/transactions/${row.id}${cardSuffix}`)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>

          <footer className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <span className="text-xs text-fg-muted">
              {formatInt(from)} - {formatInt(to)} of {totalText}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                disabled={offset === 0 || loading}
                onClick={() => setOffset((value) => Math.max(0, value - PAGE_SIZE))}
              >
                <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2.25} />
                Prev
              </Button>
              <Button
                size="sm"
                disabled={!data?.has_more || loading}
                onClick={() => setOffset((value) => value + PAGE_SIZE)}
              >
                Next
                <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.25} />
              </Button>
            </div>
          </footer>
        </section>
      </div>
    </>
  )
}

/** Смена сортировки всегда возвращает на первую страницу — иначе 101-я строка
 *  нового порядка показалась бы первой, и это выглядело бы как пропажа данных. */
function onSort(
  setSortState: (next: SortState) => void,
  setOffset: (value: number) => void,
) {
  return (next: SortState) => {
    setSortState(next)
    setOffset(0)
  }
}

// ── строки ───────────────────────────────────────────────────────────────────

function TransactionRow({
  row,
  suffix,
  onOpen,
}: {
  row: Transaction
  suffix: string
  onOpen: () => void
}) {
  const tags = splitTags(row.tags)

  return (
    // Строка — не кнопка: с клавиатуры в карточку ведёт ссылка в колонке ID,
    // она же и есть настоящий переход. Клик по строке — удобство для мыши,
    // поэтому ни роли, ни таб-стопа у неё нет: пустой таб-стоп, дублирующий
    // ссылку, только мешал бы обходу таблицы с клавиатуры.
    <TableRow className="cursor-pointer" onClick={onOpen}>
      <TableCell className="w-9" onClick={(event) => event.stopPropagation()}>
        <BulkCheckbox />
      </TableCell>
      <TableCell>
        <Link
          to={`/transactions/${row.id}${suffix}`}
          onClick={(event) => event.stopPropagation()}
          className="text-fg transition-colors duration-150 ease-snap hover:text-accent hover:underline"
        >
          {formatText(row.transaction_id)}
        </Link>
      </TableCell>
      <TableNumeric className="font-medium">{formatScore(row.fraud_score)}</TableNumeric>
      <SeonScoreCell row={row} />
      <TableCell>
        <StateCell state={row.state} />
      </TableCell>
      <TableCell className="text-fg-muted">{formatText(row.user_id)}</TableCell>
      <TableCell className="text-fg">{formatText(row.user_fullname)}</TableCell>
      {/* Сумма приведена к евро на стороне выгрузки — колонка `transaction_amount_eur`. */}
      <TableNumeric>{formatAmount(row.transaction_amount_eur, "EUR")}</TableNumeric>
      <TableCell className="whitespace-nowrap text-xs">
        <span className="block text-fg">{formatDate(row.date)}</span>
        <span className="block text-fg-subtle">{formatTime(row.date)}</span>
      </TableCell>
      <TableCell className="text-fg-muted">{formatText(row.label)}</TableCell>
      <TableCell className="text-fg-muted">{formatText(row.transaction_status)}</TableCell>
      <TableCell className="text-fg-muted">{formatText(row.assignee)}</TableCell>
      <TableCell>
        {tags.length === 0 ? (
          <span className="text-fg-subtle">{EMPTY}</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {tags.map((tag) => (
              <span
                key={tag}
                className="rounded-sm border border-line bg-surface-sunken px-1.5 py-px text-xs leading-4 text-fg-muted"
              >
                {tag}
              </span>
            ))}
          </span>
        )}
      </TableCell>
    </TableRow>
  )
}

/**
 * Колонка «SEON» — временно, AMEND-60. Число — только у живой строки (двойник)
 * с парой. У исторической строки `Score` уже и есть балл SEON из выгрузки:
 * повтор того же числа рядом читался бы как «полное совпадение», которого никто
 * не мерил. Прочерк в обоих случаях объясняет себя по наведению.
 */
function SeonScoreCell({ row }: { row: Transaction }) {
  if (row.id.startsWith(TWIN_ID_PREFIX) && row.seon_fraud_score !== null) {
    return <TableNumeric>{formatScore(row.seon_fraud_score)}</TableNumeric>
  }
  const reason = row.id.startsWith(TWIN_ID_PREFIX)
    ? "SEON ещё не прислал ответ / не склеилось"
    : "в истории Score — балл SEON из выгрузки"
  return (
    <TableNumeric>
      <InfoTip label={reason}>
        <span aria-label={reason} className="cursor-help text-fg-subtle">
          {EMPTY}
        </span>
      </InfoTip>
    </TableNumeric>
  )
}

/**
 * STATE — цветная точка и подпись, как в образце. Цвет берётся по значению из
 * данных; незнакомое значение остаётся серым и **всё равно показывается**:
 * спрятать его значило бы соврать о том, что лежит в базе.
 */
function StateCell({ state }: { state: string | null }) {
  if (!state) return <span className="text-fg-subtle">{EMPTY}</span>

  const tone = STATE_TONES[state.toUpperCase()] ?? "bg-fg-subtle"
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span className={cn("inline-block h-1.5 w-1.5 shrink-0 rounded-full", tone)} />
      <span className="text-xs font-semibold uppercase tracking-[0.04em] text-fg">
        {state}
      </span>
    </span>
  )
}

/**
 * Чекбокс массового выбора. Действия за ним нет, и это видно: он выключен и
 * объясняет причину по наведению. Живой чекбокс, за которым нет ни одного
 * массового действия, обещал бы функцию, которой в этой сборке не существует.
 */
function BulkCheckbox() {
  return (
    <InfoTip label="Bulk actions on transactions are not part of this build.">
      <span
        role="checkbox"
        aria-checked={false}
        aria-disabled
        tabIndex={-1}
        className="inline-flex h-3.5 w-3.5 shrink-0 cursor-not-allowed items-center justify-center rounded-sm border border-line-strong bg-surface opacity-45"
      />
    </InfoTip>
  )
}

function LoadingRows() {
  return (
    <>
      {Array.from({ length: 12 }, (_, row) => (
        <TableRow key={row} className="hover:bg-transparent">
          {Array.from({ length: COLUMNS }, (_, column) => (
            <TableCell key={column}>
              <Skeleton className="h-3 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}
